import { memo, useCallback, useMemo } from "react";

import { Text } from "@react/components/Text";
import { VariantSelector } from "@react/components/VariantSelector/VariantSelector.data";
import { findSelectedOption } from "@react/utilities";
import {
  type CartProductVariant,
  type CartRenderVariantOptions,
} from "@tablescape/state";
import { type TTailwindString, tw } from "classnames";

type Props = {
  productName?: string;
  selected: CartProductVariant | undefined;
  setSelected: (variant: CartProductVariant["id"]) => void;
  showProductName?: boolean;
  showThumbnail?: boolean;
  size?: "small" | "large";
  variants: CartProductVariant[];
};

const styles = {
  label: ({ active, checked }: { active: boolean; checked: boolean }) =>
    tw(
      "flex",
      "flex-1",
      "items-center",
      "justify-center",
      "whitespace-nowrap",
      "rounded",
      "border",
      "border-solid",
      "border-interactive-char",
      "bg-char-50",
      "h-[42px]" as TTailwindString,
      "px-2",
      "py-2.5",
      "text-primary",
      "no-underline",
      "transition-colors",
      "hover:bg-interactive-char",
      "hover:text-white",
      "focus:outline-none",
      "focus-visible:ring",
      "focus:ring-offset-1",
      "focus:ring-2",
      "focus:ring-interactive-outline",
      {
        "bg-interactive-char": active || checked,
        "text-white": active || checked,
      }
    ),
};

const GenericVariantSelectorBase = ({
  productName,
  selected,
  setSelected,
  showProductName,
  showThumbnail,
  size = "small",
  variants,
}: Props) => {
  const renderOption = useCallback(
    ({ active, checked, currentOption }: CartRenderVariantOptions) => {
      return (
        <div
          className={styles.label({
            active,
            checked,
          })}
        >
          {currentOption && <Text as="body2">{currentOption.value}</Text>}
        </div>
      );
    },
    []
  );

  const genericSelectedOption = useMemo(
    () =>
      selected
        ? findSelectedOption({ type: "generic", variant: selected })
        : false,
    [selected]
  );

  return genericSelectedOption ? (
    <VariantSelector
      productName={productName}
      renderOption={renderOption}
      selected={selected}
      selectedOption={genericSelectedOption}
      setSelected={setSelected}
      showProductName={showProductName}
      showThumbnail={showThumbnail}
      size={size}
      type="generic"
      variants={variants}
    />
  ) : null;
};

export const GenericVariantSelector = memo(GenericVariantSelectorBase);
